import React, { useState, useEffect } from "react";
import { useAuth0Context } from "@/contexts/Auth0Context";

interface AuthenticatedImageProps {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  onError?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  onClick?: () => void;
}

export const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({
  src,
  alt,
  className,
  style,
  onError,
  onClick,
}) => {
  const [imageSrc, setImageSrc] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { getAccessToken, isAuthenticated } = useAuth0Context();

  useEffect(() => {
    let isMounted = true;
    let blobUrl: string | null = null;

    const fetchImage = async () => {
      try {
        setLoading(true);
        setError(false);

        // Only fetch if it's an API URL that needs authentication
        if (src.includes("/api/image/")) {
          if (!isAuthenticated) {
            throw new Error("User not authenticated");
          }

          const token = await getAccessToken();
          if (!token) {
            throw new Error("No access token available");
          }

          const response = await fetch(src, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const blob = await response.blob();
          blobUrl = URL.createObjectURL(blob);

          if (isMounted) {
            setImageSrc(blobUrl);
            setLoading(false);
          }
        } else {
          // For non-API URLs, use directly
          if (isMounted) {
            setImageSrc(src);
            setLoading(false);
          }
        }
      } catch (err) {
        console.error("Error fetching authenticated image:", err);
        if (isMounted) {
          setError(true);
          setLoading(false);
          if (onError) {
            onError(err as any);
          }
        }
      }
    };

    fetchImage();

    return () => {
      isMounted = false;
      // Clean up blob URL to prevent memory leaks
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [src, getAccessToken, isAuthenticated, onError]);

  if (loading) {
    return (
      <div
        className={className}
        style={{
          ...style,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f3f4f6",
          color: "#6b7280",
          fontSize: "12px",
        }}
      >
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={className}
        style={{
          ...style,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#fef2f2",
          color: "#dc2626",
          border: "1px solid #fecaca",
          fontSize: "12px",
        }}
        onClick={onClick}
      >
        Failed to load
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      style={style}
      onClick={onClick}
      onError={onError}
    />
  );
};

export default AuthenticatedImage;
